import {useEffect, useState} from 'react';

// Dark mode for the whole panel, remembered per browser. public/index.html
// reads the same key before the app loads (the `theme-dark-boot` class on
// <html> and `theme-dark` on <body>) so a dark page never flashes white; once
// the app takes over, this keeps the `theme-dark` class on <body>, which
// drives the styles (shared/styles/dark.less).
//
// One shared state: the navbar's sun/moon button and the user menu's switch
// (small screens) always agree.
export const THEME_KEY = 'ahlan-theme';
const DARK_CLASS = 'theme-dark';
const BOOT_CLASS = 'theme-dark-boot';

const readDark = () => {
  try {
    return localStorage.getItem(THEME_KEY) === 'dark';
  } catch (error) {
    return false;
  }
};

let current = readDark();
const listeners = new Set<(dark: boolean) => void>();

const apply = (dark: boolean) => {
  document.body.classList.toggle(DARK_CLASS, dark);
  document.documentElement.classList.remove(BOOT_CLASS);
};

export const setDarkMode = (dark: boolean) => {
  current = dark;
  apply(dark);
  try {
    localStorage.setItem(THEME_KEY, dark ? 'dark' : 'light');
  } catch (error) {
    // Storage unavailable: the choice lasts for this visit only.
  }
  listeners.forEach((listener) => listener(dark));
};

export const useDarkMode = (): [boolean, (dark: boolean) => void] => {
  const [dark, setDark] = useState<boolean>(current);

  useEffect(() => {
    listeners.add(setDark);
    apply(current);
    setDark(current);
    return () => {
      listeners.delete(setDark);
      // Signed out (the panel's header is gone): the sign-in pages are light.
      if (listeners.size === 0) document.body.classList.remove(DARK_CLASS);
    };
  }, []);

  return [dark, setDarkMode];
};
